<!-- /////////////////////////////////////////////////////////////////////////// TEMPLATE -->

<template>

    <teleport to="body">
        <lsn-modal :is-open="true" class="mt-[10px]">

            <div class="sl-prompt sl-prompt--info w-[700px]">
                <div class="sl-prompt__contents">

                    <!-- Title -->
                    <div v-if="user.id" class="sl-prompt__title">
                        {{ $t('user_form.title.update') }}
                    </div>
                    <div v-else class="sl-prompt__title">
                        {{ $t('user_form.title.create') }}
                    </div>

                    <!-- Form -->
                    <div class="sl-prompt__text">

                        <!-- first line -->
                        <div class="flex mb-2">
                            <!-- first name -->
                            <lsn-input
                                v-model.trim="user.first_name"
                                :label="$t('user_form.first_name.label')"
                                :error="errors?.first_name"
                                autocomplete="off"
                                :required="true"
                                class="lsn-form__wrapper--padless w-1/2 mr-2 text-sm"
                            />

                            <!-- last name -->
                            <lsn-input
                                v-model.trim="user.last_name"
                                :label="$t('user_form.last_name.label')"
                                :error="errors?.last_name"
                                autocomplete="off"
                                :required="true"
                                class="lsn-form__wrapper--padless w-1/2 ml-2 text-sm"
                            />
                        </div>

                        <!-- second line -->
                        <div class="flex mb-2">
                            <!-- username -->
                            <lsn-input
                                v-model.trim="user.username"
                                :label="$t('user_form.username.label')"
                                :error="errors?.username"
                                autocomplete="off"
                                name="new-username"
                                :required="true"
                                class="lsn-form__wrapper--padless w-1/2 mr-2 text-sm"
                            />

                            <!-- password -->
                            <lsn-input
                                v-model.trim="user.password"
                                :error="errors?.password"
                                autocomplete="off"
                                :required="true"
                                :placeholder="(user.id) ? $t('user_form.password.placeholder') : ''"
                                class="lsn-form__wrapper--padless w-1/2 ml-2 text-sm"
                            >
                                <template #label>
                                    {{ $t('user_form.password.label') }}
                                    <span class="lsn-form_label--required">*</span>
                                    <span class="ml-2 text-primary-400 cursor-pointer" @click="setPassword">
                                        {{ $t('common.generate') }}
                                    </span>
                                </template>
                            </lsn-input>
                        </div>

                        <!-- third line -->
                        <div class="flex mb-8">
                            <!-- e-mail -->
                            <lsn-input
                                v-model.trim="user.email"
                                :label="$t('user_form.email.label')"
                                :error="errors?.email"
                                autocomplete="off"
                                :required="true"
                                class="lsn-form__wrapper--padless w-1/2 mr-2 text-sm"
                            />
                            <!-- language -->
                            <lsn-select
                                v-model="user.configurations.language"
                                :label="$t('user_form.language.label')"
                                :error="errors?.configurations?.language"
                                :options="languages"
                                ovalue="code"
                                :required="true"
                                class="lsn-form__wrapper--padless w-1/4 ml-2 text-sm"
                            />
                            <!-- is active -->
                            <lsn-switch
                                v-if="user.id"
                                v-model="user.is_active"
                                :label="$t('user_form.is_active.label')"
                                :error="errors?.is_active"
                                class="lsn-form__wrapper--padless w-1/4 text-sm text-center mt-7"
                            />
                            <div v-else class="w-1/4" /> <!-- align purpose -->
                        </div>

                        <!-- fourth line (roles) -->
                        <div class="grid grid-cols-2 gap-4 p-4" :class="{'bg-red-100': errors?.role}">
                            <fitter-without-prices
                                v-can="'user:create:fitter-without-prices'"
                                :class="{'sl-role-card--active': user.role == 'ADAPTATOR_WITHOUT_PRICES'}"
                                @click="setRole('ADAPTATOR_WITHOUT_PRICES')"
                            />
                            <fitter
                                v-can="'user:create:fitter'"
                                :class="{'sl-role-card--active': user.role == 'ADAPTATOR_WITH_PRICES'}"
                                @click="setRole('ADAPTATOR_WITH_PRICES')"
                            />
                            <manager-without-prices
                                v-can="'user:create:manager-without-prices'"
                                :class="{'sl-role-card--active': user.role == 'MANAGER_WITHOUT_PRICES'}"
                                @click="setRole('MANAGER_WITHOUT_PRICES')"
                            />
                            <manager
                                v-can="'user:create:manager'"
                                :class="{'sl-role-card--active': user.role == 'MANAGER_WITH_PRICES'}"
                                @click="setRole('MANAGER_WITH_PRICES')"
                            />
                        </div>

                        <!-- fifth line (roles) | Only on user creation -->
                        <div v-if="!user.id" class="mt-4">
                            <lsn-switch
                                v-model="options.send_welcome_email"
                                :label="$t('user_form.send_welcome_email.label')"
                                :error="errors?.send_welcome_email"
                                class="lsn-form__wrapper--padless text-sm text-center"
                            />
                        </div>
                        <div v-else-if="user.id && user.password" class="mt-4">
                            <lsn-switch
                                v-model="options.send_password_changed_email"
                                :label="$t('user_form.send_password_changed_email.label')"
                                :error="errors?.send_password_changed_email"
                                class="lsn-form__wrapper--padless text-sm text-center"
                            />
                        </div>

                    </div>

                </div>

                <!-- Buttons -->
                <div class="sl-prompt__actions">
                    <button class="sl-prompt__button sl-prompt__button--white" @click.stop="cancel">
                        {{ $t('common.cancel') }}
                    </button>
                    <button v-if="!saving" class="sl-prompt__button sl-prompt__button--primary" @click.stop="save">
                        {{ $t('common.save') }}
                    </button>
                    <button v-else class="sl-prompt__button sl-prompt__button--disabled">
                        {{ $t('common.saving') }}
                    </button>
                </div>

            </div>

        </lsn-modal>
    </teleport>

</template>

<!-- /////////////////////////////////////////////////////////////////////////// SCRIPT -->

<script>

import FitterWithoutPrices from './roles/FitterWithoutPrices.vue';
import Fitter from './roles/Fitter.vue';
import ManagerWithoutPrices from './roles/ManagerWithoutPrices.vue';
import Manager from './roles/Manager.vue';
import String from '@/utils/String';

export default
{
    name: 'UserManagement',

    components:
    {
        FitterWithoutPrices,
        Fitter,
        ManagerWithoutPrices,
        Manager,
    },

    data()
    {
        return {
            user:    null,
            options: null,
            errors:  null,
            saving:  false,
        };
    },

    computed:
    {
        entityUsers()
        {
            return this.$store.state.users.entityUsers;
        },

        cEntity()
        {
            return this.$store.state.account.cEntity;
        },

        languages()
        {
            return this.$const.languages.map(language =>
            {
                return {
                    code:  language.code,
                    label: this.$t(`languages.${language.code}.label`),
                };
            });
        },
    },

    created()
    {
        let user;
        const options = {};

        if(this.$route.params.user_id)
        {
            const eUser = this.$store.getters['users/getById'](this.$route.params.user_id);

            if(!eUser)
            {
                throw new Error(`User with id ${id} does not exists within entity ${this.cEntity.id}.`);
            }

            user = Object.assign({}, { configurations: { language: null } }, eUser);
            options.send_password_changed_email = false;
        }
        else
        {
            user = {
                id:         null,
                is_active:  true,
                first_name: null,
                last_name:  null,
                username:   null,
                password:   this.generatePassword(),
                email:      null,
                role:       null,
                configurations:
                {
                    language: this.cEntity.lng,
                },
            };

            options.send_welcome_email = true;

            // Only if it is the first user of the current entity.
            if(this.entityUsers.length == 0 && this.cEntity.contact_person)
            {
                try
                {
                    const [, first_name, last_name] = this.cEntity.contact_person.split(' ');

                    user.first_name = first_name;
                    user.last_name = last_name;

                    const username = first_name + '.' + last_name;
                    user.username = String.removeAccents(username).toLowerCase();
                }
                catch(err)
                {
                    console.log(err);
                }
            }
        }

        this.user = user;
        this.options = options;
    },

    methods:
    {
        cancel()
        {
            this.close();
        },

        save()
        {
            this.saving = true;

            const action = (this.user.id) ? 'users/update' : 'users/create';

            this.$store.dispatch(action, {
                user:    this.user,
                options: this.options,
            }).then(() =>
            {
                this.close();
            }).catch(err =>
            {
                this.errors = err.response.errors;
            }).finally(() =>
            {
                this.saving = false;
            });
        },

        close()
        {
            this.$router.push({
                name:   'user-management',
                params: this.$route.params,
            });
        },

        setRole(role)
        {
            this.user.role = role;
        },

        generatePassword()
        {
            return String.password();
        },

        setPassword()
        {
            this.user.password = this.generatePassword();
        },
    },
};

</script>

<!-- /////////////////////////////////////////////////////////////////////////// STYLE -->

<style scoped>
@reference "#main.css";


:deep(.lsn-form__switch-wrapper)
{
    margin-top: -1px;
    @apply ml-2;
}

</style>
